import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Languages, Save, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { presentationHubAPI } from '@/shared/services/api';
import { getPresentationRouteByDeck, PRESENTATION_HUB_PUBLIC_ROUTES } from '@/modules/crm/presentation-hub/presentationHubRoutes';

function normalizeRows(rows = []) {
  return rows.map((row, index) => ({
    id: `${row.lang}:${row.key}:${index}`,
    lang: row.lang,
    key: row.key,
    value: row.value ?? '',
    valueType: row.valueType || 'str',
  }));
}

export default function PresentationHubEditor() {
  const { deckId } = useParams();
  const route = getPresentationRouteByDeck(deckId);
  const [payload, setPayload] = useState(null);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [activeLang, setActiveLang] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    presentationHubAPI.getDeckTranslations(deckId)
      .then((data) => {
        if (!mounted) return;
        setPayload(data);
        setRows(normalizeRows(data.rows));
      })
      .catch((error) => {
        toast.error(error.message || 'Unable to load presentation editor');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [deckId]);

  const languages = payload?.deck?.languages || route?.languages || [];
  const publicRoute = route?.publicPath || '/crm/presentation-hub';
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      const langOk = activeLang === 'all' || row.lang === activeLang;
      const queryOk = !needle || row.key.toLowerCase().includes(needle) || row.value.toLowerCase().includes(needle);
      return langOk && queryOk;
    });
  }, [rows, activeLang, query]);

  const updateRow = (id, value) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, value } : row)));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await presentationHubAPI.saveDeckTranslations(deckId, {
        rows: rows.map(({ lang, key, value }) => ({ lang, key, value })),
      });
      toast.success('Presentation draft saved');
    } catch (error) {
      toast.error(error.message || 'Unable to save presentation draft');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      await presentationHubAPI.saveDeckTranslations(deckId, {
        rows: rows.map(({ lang, key, value }) => ({ lang, key, value })),
      });
      await presentationHubAPI.publishDeckTranslations(deckId);
      toast.success('Presentation published');
    } catch (error) {
      toast.error(error.message || 'Unable to publish presentation');
    } finally {
      setSaving(false);
    }
  };

  if (!route) {
    return (
      <div className="space-y-4">
        <Button variant="outline" asChild>
          <Link to="/crm/presentation-hub">Back to Presentation Hub</Link>
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Unknown presentation</CardTitle>
            <CardDescription>
              Available decks: {PRESENTATION_HUB_PUBLIC_ROUTES.map((item) => item.sourceDeck).join(', ')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="presentation-hub-editor">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button variant="ghost" className="-ml-3 mb-2 gap-2" asChild>
            <Link to="/crm/presentation-hub">
              <ArrowLeft className="h-4 w-4" />
              Presentation Hub
            </Link>
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{route.name}</h1>
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Own Admin</Badge>
          </div>
          <p className="mt-1 text-muted-foreground">
            Edit this presentation as a native EzERP subsystem. Public viewers still use the no-login slug.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <a href={publicRoute} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4" />
              Preview Public
            </a>
          </Button>
          <Button variant="outline" className="gap-2" onClick={saveDraft} disabled={saving || loading}>
            <Save className="h-4 w-4" />
            Save Draft
          </Button>
          <Button className="gap-2" onClick={publish} disabled={saving || loading}>
            <Send className="h-4 w-4" />
            Save & Publish
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50">
                <ShieldCheck className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Admin Scope</p>
                <p className="font-semibold">Deck-specific</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Languages className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Languages</p>
                <p className="font-semibold">{languages.join(', ')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Public Slug</p>
            <p className="mt-1 font-mono text-sm">{publicRoute}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Presentation Content</CardTitle>
          <CardDescription>
            Changes are saved as EzERP Presentation Hub data and published back to the public deck viewer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search slide key or text..."
              className="md:max-w-md"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeLang === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveLang('all')}
              >
                All
              </Button>
              {languages.map((lang) => (
                <Button
                  key={lang}
                  variant={activeLang === lang ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveLang(lang)}
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="rounded-lg border p-8 text-center text-muted-foreground">Loading presentation editor...</div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <div className="grid grid-cols-[88px_minmax(180px,320px)_1fr] border-b bg-slate-50 px-4 py-3 text-sm font-medium text-slate-600">
                <div>Lang</div>
                <div>Key</div>
                <div>Value</div>
              </div>
              <div className="max-h-[620px] overflow-auto">
                {visibleRows.map((row) => (
                  <div key={row.id} className="grid grid-cols-[88px_minmax(180px,320px)_1fr] gap-3 border-b px-4 py-3 last:border-b-0">
                    <div>
                      <Badge variant="outline">{row.lang.toUpperCase()}</Badge>
                    </div>
                    <div className="break-words font-mono text-xs text-slate-600">{row.key}</div>
                    <textarea
                      value={row.value}
                      onChange={(event) => updateRow(row.id, event.target.value)}
                      className="min-h-[44px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                ))}
                {!visibleRows.length && (
                  <div className="p-8 text-center text-muted-foreground">No content rows match this filter.</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
